import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { ocenBrakSprzedazy } from '@/lib/brakSprzedazy';
import { klasyfikacjaKatalogu, klasyfikacjaSprzedazy, swiezoscKatalogu, swiezoscSprzedazy } from '@/lib/pochodzenieDanych';
import { opiszDatyKarty } from '@/lib/dataKarty';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { Karta } from '@/components/Karta';
import { PasSekcji } from '@/components/PasSekcji';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { formatCount, formatGrosze } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useDishCatalog } from '@/lib/useAnalytics';
import type { DishCatalogResponse } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * KATALOG DAŃ — migracja do języka „SYGNAŁ" (kierunek C, R5-15-009)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * LICZBA-BOHATER: ile dań z katalogu NIE MA ANI JEDNEJ SPRZEDAŻY, na tle całego katalogu („4 / 17").
 *
 * Dlaczego tego pytania nie dało się dotąd zadać: jedyną listą dań w systemie były pozycje
 * zamówień, czyli zbiór „co kupiono" — a danie, którego nikt nie kupił, w takim zbiorze
 * NIE ISTNIEJE. Brakowało nie danych, tylko właściwej tabeli.
 *
 * CZEGO TU ŚWIADOMIE NIE MA: portfolio w kwadrantach (wartość × wolumen). Kwadranty wymagają
 * danych sprzedażowych z okna czasowego, a ta odpowiedź ich nie zawiera — niesie wyłącznie
 * bezokienny licznik pozycji. Puste kwadranty to ładna siatka bez informacji.
 *
 * TOŻSAMOŚĆ vs DOSTĘPNOŚĆ: restauracje kopiują kartę na kolejne dni. Wiersz menu dziennego nie
 * oznacza nowego produktu — katalog jest kluczowany kanonicznym identyfikatorem dania, więc to
 * samo danie widziane w piętnastu szkołach daje JEDEN wpis, a nie piętnaście „nowości".
 */
/**
 * Opis źródła katalogu. Zwraca ZDANIE, nigdy interpolacji brakującej wartości.
 *
 * Kontrakt deklaruje `source` jako pole WYMAGANE — a mimo to warto się przed nim bronić,
 * i to nie z ostrożności, tylko z powodu, który już wystąpił w tym produkcie trzy razy:
 * gdy backend nie odda pola, `${data.source}` renderuje łańcuch „undefined" wprost w
 * OPISIE ŹRÓDŁA, czyli w jedynym miejscu, które mówi czytelnikowi, skąd wzięła się liczba.
 * „CRM /dictionary/dishes → undefined" wygląda jak nazwa źródła i przez to jest gorsze
 * niż jawne „nieokreślone": czytelnik nie ma jak poznać, że coś się urwało.
 */
function zrodloKatalogu(source: string | null | undefined): string {
  const nazwa = typeof source === "string" && source.trim() !== "" ? source : "źródło nieokreślone w odpowiedzi";
  return `CRM /dictionary/dishes → ${nazwa}`;
}

export function DishCatalogPage() {
  const [szukaj, setSzukaj] = useState('');
  const fraza = szukaj.trim();
  const { data, isLoading, error } = useDishCatalog(fraza ? { search: fraza } : {});

  return (
    <Box>
      <NaglowekEkranu
        tytul="Katalog dań"
        opis={
          <>
            Na jakie pytanie odpowiada ten ekran: <strong>czego mamy w ofercie, a się nie sprzedaje</strong>. To
            jest <strong>rejestr oferty</strong>, nie lista dań ze sprzedażą — dotąd jedyną listą dań były pozycje
            zamówień, czyli zbiór „co kupiono", a danie, którego nikt nie kupił, w takim zbiorze NIE ISTNIEJE.
          </>
        }
        akcja={
          <TextField
            size="small"
            label="Szukaj w nazwie"
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
            sx={{ width: 260 }}
          />
        }
      />

      <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS' }} />

      {isLoading ? <LoadingState /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy słownik nie odpowiada, nie pokazujemy listy zastępczej."
        />
      ) : null}

      {/* PUSTY ZBIÓR rozstrzygamy kanonicznym predykatem, nie ad-hoc `length === 0` — reguła
          „zero to nie to samo co pusto" jest niezmiennikiem produktu, a nie szczegółem ekranu. */}
      {data && czyPustyZbior({ wierszy: data.count, bytow: data.dishes.length }) ? (
        fraza ? (
          /* Filtr, który nic nie zwrócił, to NIE jest pusty katalog. Nazwanie tego „katalog jest
             pusty" byłoby wnioskiem o stanie zasilenia wyciągniętym z frazy wyszukiwania. */
          <EmptyState
            title="Żadne danie nie pasuje do frazy"
            description={`Fraza „${fraza}" nie ma dopasowania w nazwach dań. To jest wynik filtra, a nie stan katalogu — wyczyść pole, żeby zobaczyć cały rejestr.`}
          />
        ) : (
          <EmptyState
            title="Rejestr nie został zasilony"
            description="Katalog dań jest pusty — źródło nie zwróciło ani jednego wpisu. To nie znaczy, że restauracje nie mają oferty; znaczy, że jej tu nie zaimportowano."
          />
        )
      ) : null}

      {data && !czyPustyZbior({ wierszy: data.count, bytow: data.dishes.length }) ? (
        <WynikKatalogu data={data} fraza={fraza} />
      ) : null}
    </Box>
  );
}

/**
 * Wynik dla niepustego katalogu. Wydzielony, żeby `data` było tu bezwarunkowo obecne — dowód ma
 * być budowany z tych samych pól, które zasilają liczbę na ekranie, a nie z osobnego opisu.
 */
function WynikKatalogu({ data, fraza }: { data: DishCatalogResponse; fraza: string }) {
  const dania = data.dishes;

  /*
   * ZIARNO WYNIKU. Wiersze to dania, bytami w rozbiciu są restauracje. Ten dobór nie jest
   * ozdobny: zero dań i zero restauracji to pusty zbiór (nie ma czego liczyć), natomiast dania
   * bez ani jednej przypisanej restauracji to problem złączenia słowników — inna diagnoza,
   * inna reakcja. Reguła i jej uzasadnienie mieszkają w lib/pustyZbior.ts.
   */
  const restauracje = useMemo(
    () => new Set(dania.filter((d) => d.restaurant_id !== null).map((d) => d.restaurant_id)).size,
    [dania],
  );
  const ziarno = { wierszy: data.count, bytow: restauracje };

  const bezSprzedazy = wartoscDoPokazania(data.without_sales_count, ziarno);
  const wKatalogu = wartoscDoPokazania(data.count, ziarno);

  const zeSprzedaza = useMemo(() => dania.filter((d) => d.has_sales).length, [dania]);
  const wOfercie = useMemo(() => dania.filter((d) => d.is_active).length, [dania]);
  const bezSprzedazyWOfercie = useMemo(
    () => dania.filter((d) => !d.has_sales && d.is_active).length,
    [dania],
  );
  const bezCeny = useMemo(() => dania.filter((d) => d.list_price_grosze === null).length, [dania]);
  const pozycjeRazem = useMemo(() => dania.reduce((suma, d) => suma + d.order_item_rows, 0), [dania]);
  const ostatnieWidzenie = useMemo(() => {
    const stemple = dania.map((d) => d.last_seen_at).filter((s): s is string => typeof s === 'string');
    return stemple.length ? stemple.reduce((a, b) => (a > b ? a : b)) : null;
  }, [dania]);

  /*
   * Sortowanie prezentacyjne: najpierw dania BEZ sprzedaży. Sedno ekranu ma być widoczne bez
   * przewijania. Wewnątrz obu grup zostaje kolejność ze źródła (alfabetyczna) — sort jest
   * stabilny, więc niczego innego nie przestawiamy.
   */
  const wiersze = useMemo(
    () => [...dania].sort((a, b) => Number(a.has_sales) - Number(b.has_sales)),
    [dania],
  );

  const bohater = bezSprzedazy === null || wKatalogu === null
    ? null
    : `${formatCount(bezSprzedazy)} / ${formatCount(wKatalogu)}`;

  /* Ten sam obraz — zero pozycji w całym wyniku — daje katalog dań, których nikt nie kupił,
     ORAZ rejestr sprzedaży, którego nie zasilono. Katalog tych dwóch przypadków nie rozróżnia
     i nie wolno udawać, że rozróżnia. */
  const rejestrSprzedazyMilczy = pozycjeRazem === 0;

  /*
   * ROZSTRZYGNIĘCIE, KTÓREGO EKRAN WCZEŚNIEJ NIE MIAŁ.
   *
   * „13 z 13 dań bez sprzedaży" było ogłaszane jako liczba-bohater i podświetlane barwą uwagi.
   * Arytmetycznie bez zarzutu — ale rejestr sprzedaży był PUSTY, więc to zdanie nie mówiło nic
   * o daniach, tylko o braku danych. Właściciel czytał je jako diagnozę karty menu.
   *
   * Backend podaje teraz liczebność CAŁEGO rejestru sprzedaży, więc da się odróżnić „nikt tego
   * nie kupił" od „nie mamy czego liczyć". Dopiero to drugie wolno pokazywać jako sygnał.
   */
  const ocenaBraku = ocenBrakSprzedazy({
    wierszySprzedazyOgolem: data.sales_source_rows ?? null,
    danWKatalogu: data.count,
    danBezSprzedazy: data.without_sales_count,
  });
  const meta = data.catalog_sync?.source_meta ?? null;
  const klasyfikacjaZrodlaKatalogu = klasyfikacjaKatalogu(meta);
  const swiezoscZrodlaKatalogu = swiezoscKatalogu(meta, data.catalog_sync?.synced_at ?? null);

  const ograniczenia: string[] = [
    'Licznik pozycji sprzedaży NIE MA okna czasowego — obejmuje cały rejestr pozycji, jaki CRM ma zmirrorowany. Danie sprzedane raz, dawno temu, nie jest tu „bez sprzedaży".',
    'Katalog jest migawką bieżącej oferty, nie historią. Danie wycofane przed ostatnim odczytem może w nim w ogóle nie wystąpić — dlatego każdy wpis niesie datę ostatniego widzenia.',
    'Katalog opisuje OFERTĘ, nie sprzedaż. Świeżość karty nie mówi niczego o świeżości transakcji — to osobne źródło i osobny status.',
  ];
  if (fraza) {
    ograniczenia.unshift(
      `Wynik jest zawężony frazą „${fraza}". Mianownik „w katalogu" dotyczy wyłącznie dań pasujących do frazy, a nie całego katalogu.`,
    );
  }
  // Wcześniej stała tu heurystyka „zero pozycji W WYNIKU", która nie odróżniała pustego rejestru
  // od zawężenia filtrem. Backend podaje teraz liczebność CAŁEGO rejestru, więc mówimy wprost,
  // która z dwóch sytuacji zaszła, zamiast opisywać obie naraz.
  ograniczenia.unshift(ocenaBraku.zdanie);
  if (rejestrSprzedazyMilczy && ocenaBraku.podstawa === 'ZMIERZONY_BRAK') {
    ograniczenia.unshift(
      'W tym WYNIKU nie ma ani jednej pozycji sprzedaży, choć rejestr sprzedaży nie jest pusty — to skutek zawężenia, nie braku danych.',
    );
  }
  if (restauracje === 0) {
    ograniczenia.unshift(
      'Żaden wpis w wyniku nie ma przypisanej restauracji — to jest stan złączenia słowników, a nie cecha oferty.',
    );
  }

  const dowodBezSprzedazy: Dowod = {
    tytul: ocenaBraku.czySygnal ? 'Dania bez sprzedaży na tle katalogu' : 'Wpisy w katalogu dań',
    etykietaWartosci: ocenaBraku.czySygnal ? 'Bez sprzedaży / w katalogu' : 'Dań w katalogu',
    wartosc: ocenaBraku.czySygnal ? bohater : (wKatalogu === null ? null : formatCount(wKatalogu)),
    // Bez osi czasu i bez podstawy kwotowej — ta liczba nie jest ani pieniądzem, ani okresem.
    podstawa: { volume: 'ORDER_ITEM_ROWS' },
    zrodlo: meta ? `${meta.zrodlo} → CRM /dictionary/dishes` : zrodloKatalogu(data.source),
    klasyfikacja: klasyfikacjaZrodlaKatalogu,
    swiezosc: swiezoscZrodlaKatalogu,
    oknoBiezace: 'brak okna — migawka katalogu, licznik pozycji z całego rejestru',
    oknoPorownawcze: null,
    wyliczenie:
      'Katalog (crm_dishes) złączony LEWOSTRONNIE z liczbą żywych pozycji zamówienia (crm_order_items) po kanonicznym identyfikatorze dania. „Bez sprzedaży" = zero pozycji. Złączenie wewnętrzne zamieniłoby katalog z powrotem w listę sprzedanych i zniweczyło sens ekranu.',
    szczegoly: [
      ['Dania w wyniku', formatCount(data.count)],
      ['Z tego bez sprzedaży', formatCount(data.without_sales_count)],
      ['Bez sprzedaży, a wciąż w ofercie', formatCount(bezSprzedazyWOfercie)],
      ['Pozycje sprzedaży w całym wyniku', formatCount(pozycjeRazem)],
      ['Restauracje w wyniku', formatCount(restauracje)],
      ['Filtr nazwy', fraza ? `„${fraza}"` : 'brak — cały katalog'],
      ['Najświeższe „ostatnio widziane"', ostatnieWidzenie ? dataZeStempla(ostatnieWidzenie) : 'brak stempla'],
      // ── PROWENIENCJA ODCZYTU ──────────────────────────────────────────────────────────────
      // Bez tych czterech wierszy „13 dań" jest liczbą bez wyprowadzenia, a właściciel nie ma
      // jak odróżnić tożsamości dania od wiersza menu dziennego. Karta jest KOPIOWANA na
      // kolejne dni, więc różnica między 364 a 13 to nie jest szczegół techniczny.
      ['Data karty w źródle', meta ? opiszDatyKarty(meta.data_karty) : 'nieznana — brak zapisu odczytu'],
      ['Surowe wiersze menu w źródle', meta ? formatCount(meta.surowe_wystapienia_menu) : 'nieznane'],
      ['Tożsamości po deduplikacji', meta ? formatCount(meta.unikalnych_dan) : formatCount(data.count)],
      ['Reguła deduplikacji', meta ? meta.regula_deduplikacji : 'meal_tuszama_id'],
      ['Szkoły w źródle / z menu / nieodczytane', meta
        ? `${formatCount(meta.szkol_w_zrodle)} / ${formatCount(meta.szkol_z_menu)} / ${formatCount(meta.szkol_nieodczytanych)}`
        : 'nieznane'],
      ['Wierszy w rejestrze sprzedaży', data.sales_source_rows === undefined
        ? 'nieznane' : formatCount(data.sales_source_rows)],
    ],
    proba: `${formatCount(data.count)} dań w katalogu`,
    ograniczenia,
    czegoNieWynika:
      'Nie wynika z tego, że danie nie sprzedaje się DZIŚ ani w wybranym oknie — licznik pozycji nie ma żadnego okna czasowego. Nie wynika też, że danie jest w ofercie: o tym mówi osobna kolumna Stan. I nie wynika, że danie jest złe — katalog nie zna ani powodu, ani tego, czy danie w ogóle trafiło do menu dnia w jakiejkolwiek szkole.',
  };

  const dowodPozycji: Dowod = {
    tytul: 'Pozycje sprzedaży w wierszu dania',
    etykietaWartosci: 'Pozycje w całym wyniku',
    wartosc: formatCount(pozycjeRazem),
    podstawa: { volume: 'ORDER_ITEM_ROWS' },
    zrodlo: zrodloKatalogu(data.source),
    klasyfikacja: klasyfikacjaSprzedazy(),
    swiezosc: swiezoscSprzedazy(),
    oknoBiezace: 'brak okna — cały rejestr pozycji',
    oknoPorownawcze: null,
    wyliczenie:
      'COUNT(*) po żywych pozycjach zamówienia (is_deleted IS NOT TRUE) zgrupowanych po identyfikatorze dania; dania bez dopasowania dostają zero.',
    szczegoly: [
      ['Dania ze sprzedażą', formatCount(zeSprzedaza)],
      ['Dania bez sprzedaży', formatCount(data.without_sales_count)],
    ],
    proba: `${formatCount(data.count)} dań w katalogu`,
    czegoNieWynika:
      'Ta kolumna nie mówi, ile PORCJI ani ile ZAMÓWIEŃ — jedna pozycja to jeden wiersz zamówienia, a system główny rozbija wybór wielu sztuk na osobne wiersze. Nie mówi też, ile pieniędzy wpłynęło (cena katalogowa to nie kwota pobrana) ani kiedy: wiersze nie są ograniczone żadnym oknem czasowym.',
  };

  return (
    <Stack spacing={3} className="sygnal-kaskada" sx={{ mt: 2 }}>
      {/*
        CO JEST LICZBĄ-BOHATEREM, ZALEŻY OD TEGO, CO DA SIĘ Z NIEJ WYWNIOSKOWAĆ.

        Przy pustym rejestrze sprzedaży „13 z 13 bez sprzedaży" jest tautologią: każde danie
        MUSI tam wyjść bez sprzedaży, niezależnie od tego, jak się sprzedaje. Ogłaszanie
        tautologii jako głównej liczby ekranu to postawienie diagnozy, której nikt nie postawił.
        Wtedy nagłówkiem jest rozmiar oferty — jedyna rzecz, którą ten ekran naprawdę zmierzył.
      */}
      <PlytaGlowna
        etykieta={ocenaBraku.czySygnal ? 'Dania bez sprzedaży na tle katalogu' : 'Wpisy w katalogu dań'}
        wartosc={ocenaBraku.czySygnal ? bohater : (wKatalogu === null ? null : formatCount(wKatalogu))}
        opis={
          <Typography variant="body2">
            {ocenaBraku.czySygnal
              ? `${formatCount(data.without_sales_count)} z ${formatCount(data.count)} dań w katalogu nie ma ani jednej pozycji sprzedaży` +
                (bezSprzedazyWOfercie > 0
                  ? `; ${formatCount(bezSprzedazyWOfercie)} z nich jest wciąż w ofercie`
                  : '')
              : ocenaBraku.zdanie}
          </Typography>
        }
        zrodlo={zrodloKatalogu(data.source)}
        okno="brak okna — migawka katalogu, licznik pozycji z całego rejestru"
        dowod={dowodBezSprzedazy}
        stopka={
          <Button
            size="small"
            component={Link}
            to="/restauracje/dania"
            sx={{ color: 'var(--tekst-na-ciemnym)' }}
          >
            zobacz, co się sprzedaje →
          </Button>
        }
        boczne={
          <>
            <MiniMetryka wartosc={formatCount(data.count)} etykieta="dań w katalogu" />
            <MiniMetryka wartosc={formatCount(zeSprzedaza)} etykieta="ze sprzedażą" />
            <MiniMetryka wartosc={formatCount(wOfercie)} etykieta="w ofercie" />
            <MiniMetryka wartosc={formatCount(bezCeny)} etykieta="bez ceny katalogowej" />
          </>
        }
      />

      {rejestrSprzedazyMilczy ? (
        <Alert severity="warning">
          W całym wyniku nie ma ani jednej pozycji sprzedaży, więc każde danie jest tu „bez sprzedaży".
          Tak samo wygląda katalog dań, których nikt nie kupił, i rejestr sprzedaży, którego nie
          zasilono — <strong>tych dwóch przypadków ta liczba nie rozróżnia</strong>. Zanim wyciągniesz
          wniosek o ofercie, sprawdź stan zasilania w <Link to="/system">Statusie połączeń</Link>.
        </Alert>
      ) : null}

      <Box>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5">Wpisy katalogu</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {/* Liczba dań BEZ sprzedaży zostaje pokazana wprost także tutaj — to jest właśnie ta
              informacja, której poprzednia lista (pozycje zamówień) nie potrafiła oddać. */}
          <Chip size="small" label={`${formatCount(data.count)} dań w katalogu`} />
          <Chip
            size="small"
            color={ocenaBraku.czySygnal ? 'warning' : 'default'}
            variant="outlined"
            label={`${formatCount(data.without_sales_count)} bez sprzedaży`}
          />
          {fraza ? <Chip size="small" variant="outlined" label={`filtr nazwy: „${fraza}"`} /> : null}
          <PrzyciskDowodu dowod={dowodPozycji} etykieta="Czym są pozycje" />
        </Stack>

        <PasSekcji
          tytul="Cały katalog"
          opis="Karta jako osobny byt, nie produkt uboczny zamówień — także to, czego nikt nie kupił."
        />

        <Karta nadtytul="Jedno danie = jeden wiersz" tytul="Katalog dań">
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mb: 1 }}>
          Jeden wiersz = jedno danie wg kanonicznego identyfikatora, niezależnie od tego, w ilu
          szkołach i dniach się pojawiło. Kolejność: najpierw dania bez sprzedaży, dalej jak w źródle.
        </Typography>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Katalog dań">
            <TableHead>
              <TableRow>
                <TableCell>Danie</TableCell>
                <TableCell>Restauracja</TableCell>
                <TableCell align="right">Cena katalogowa</TableCell>
                <TableCell align="right">Pozycje sprzedaży</TableCell>
                <TableCell>Ostatnio widziane</TableCell>
                <TableCell>Stan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wiersze.map((d) => (
                <TableRow key={d.dish_id} hover>
                  <TableCell>
                    {d.name ?? (Number.isFinite(d.dish_id) ? `#${d.dish_id}` : "danie bez nazwy i bez identyfikatora")}
                    <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
                      id {d.dish_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{d.restaurant_name ?? (d.restaurant_id ? `#${d.restaurant_id}` : '—')}</TableCell>
                  {/* Brak ceny to BRAK, nie zero — kreska, nigdy „0,00 zł". */}
                  <TableCell align="right" className="sygnal-liczba">
                    {d.list_price_grosze === null ? '—' : formatGrosze(d.list_price_grosze)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatCount(d.order_item_rows)}
                  </TableCell>
                  <TableCell className="sygnal-liczba">{dataZeStempla(d.last_seen_at)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {d.has_sales ? null : (
                        <Chip size="small" color="warning" variant="outlined" label="bez sprzedaży" />
                      )}
                      {d.is_active ? null : <Chip size="small" variant="outlined" label="poza ofertą" />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Karta>
      </Box>

      <Alert severity="info">
        Katalog pochodzi z bieżącej oferty i jest migawką, a nie pełną historią. Danie wycofane przed
        dniem ostatniego odczytu może w nim nie wystąpić — dlatego każdy wpis niesie datę ostatniego
        widzenia. Kolumna „Pozycje sprzedaży" nie ma okna czasowego: liczy cały zmirrorowany rejestr
        pozycji, a nie wybrany okres.
      </Alert>
    </Stack>
  );
}

/**
 * Data ze stempla źródła, bez przeliczania stref czasowych — pokazujemy to, co źródło zapisało.
 * Doklejanie tu godziny w czasie lokalnym sugerowałoby precyzję, której stempel nie gwarantuje.
 */
function dataZeStempla(stempel: string | null): string {
  if (!stempel) return '—';
  const dopasowanie = /^(\d{4}-\d{2}-\d{2})/.exec(stempel);
  return dopasowanie?.[1] ?? stempel;
}
